import React, { useState, useEffect } from "react";
import DragAndDropMedia from "./DragAndDrop";
import { AssetType } from "../../types/asset-type";
import { MediaType } from "../../types/mediaType";
import { ListingType } from "../../types/userListing";

interface AssetsUploaderProps {
    listingId: string;
    listingType?: ListingType | undefined;
    onAssetsUploaded: () => void;
    assetType: AssetType;
    type?: MediaType;
    uploadsEnabled: boolean;
    label?: string;
    onPendingFilesChange?: (count: number) => void;
    onActiveUploadsChange?: (count: number) => void;
    /** Show the HD resolution note inside the upload area (used for "both" media type) */
    showHdNote?: boolean;
}

const AssetsUploader: React.FC<AssetsUploaderProps> = ({
    listingId,
    listingType,
    onAssetsUploaded,
    assetType,
    type,
    uploadsEnabled,
    label,
    onPendingFilesChange,
    onActiveUploadsChange,
    showHdNote = false,
}) => {
    // The note copy under the drop zone must match where the upload actually
    // lands: shared-asset types go to /assets/signed-url, everything else is
    // listing-scoped (see useParallelUpload). The old uploadsEnabled inversion
    // showed campaign-renewal copy on shared ad-hoc uploads.
    const uploadsToSharedAssets = ["asset", "assetImage", "assetVideo", "logo"].includes(
        type ?? "image"
    );

    const [isUploading, setIsUploading] = useState(false);
    const [activeUploads, setActiveUploads] = useState(0);

    // Notify parent when active uploads change
    useEffect(() => {
        if (onActiveUploadsChange) {
            onActiveUploadsChange(activeUploads);
        }
    }, [activeUploads, onActiveUploadsChange]);

    return (
        <div className="border p-4 rounded-md">
            <h4 className="text-base font-semibold mb-2 text-primary">
                {label ?? (uploadsEnabled ? "Upload New Media" : "Upload New Assets")}
            </h4>
            {showHdNote && (
                <div className="bg-gray-100 p-3 rounded-md mb-4 flex flex-row items-center">
                    <p className="text-sm text-red-500">
                        <strong className="text-primary">Note:</strong> To capture a new video tap <strong className="text-red-500">Record Video</strong>. To use an existing file from your gallery tap <strong className="text-red-500">Select Files</strong>.
                    </p>
                </div>
            )}
            <DragAndDropMedia
                listingId={uploadsEnabled ? listingId : undefined}
                listingType={uploadsEnabled ? listingType : undefined}
                type={type || "image"}
                onUploadStart={() => {}}
                onUploadComplete={onAssetsUploaded}
                isUploading={isUploading}
                setIsUploading={setIsUploading}
                assetType={assetType}
                isAdhoc={uploadsToSharedAssets}
                onPendingFilesChange={onPendingFilesChange}
                onActiveUploadsChange={setActiveUploads}
            />
        </div>
    );
};

export default AssetsUploader;
